import React, { useState, useEffect } from "react";
import { Modal, Avatar, Spin } from "antd";
import { useDispatch } from "react-redux";
import { bulkUpdateEmployeesVerification } from "../../../core/data/redux/employeesVerificationSlice";
import { formatDateTime } from "../../../utils/dateUtils";
import { toast } from "react-toastify";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

interface BulkFaceDataRegularizeModalProps {
  open: boolean;
  items: any[];
  initialActiveId?: number;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ItemCustomConfig {
  actionType?: "accept" | "reject";
  reason?: string;
}

const BulkFaceDataRegularizeModal: React.FC<BulkFaceDataRegularizeModalProps> = ({
  open,
  items,
  initialActiveId,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const [selectedList, setSelectedList] = useState<any[]>([]);
  const [activeItemId, setActiveItemId] = useState<number | null>(null);

  // Master form state
  const [actionType, setActionType] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [reasonError, setReasonError] = useState<string>("");

  // Per-item override configs
  const [customConfigs, setCustomConfigs] = useState<Record<number, ItemCustomConfig>>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Photo caching & loading state
  const [photoCache, setPhotoCache] = useState<Record<number, { url: string; otherPhotos?: any[] }>>({});
  const [loadingPhoto, setLoadingPhoto] = useState<boolean>(false);
  const [activePhotoUrl, setActivePhotoUrl] = useState<string>("");
  const [activeOtherPhotos, setActiveOtherPhotos] = useState<any[]>([]);

  // Items checked for submission (all checked by default)
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  // Initialize state on open - select ALL items by default
  useEffect(() => {
    if (open) {
      const list = items || [];
      setSelectedList(list);
      const allIds = list.map((it) => Number(it.id ?? it.key)).filter(Boolean);
      setCheckedIds(allIds);

      setActionType("");
      setReason("");
      setReasonError("");
      setCustomConfigs({});
      setEditingItemId(null);

      // Determine initial active item
      const initialId =
        initialActiveId !== undefined && list.some((it) => (it.id ?? it.key) === initialActiveId)
          ? initialActiveId
          : list.length > 0
          ? list[0]?.id ?? list[0]?.key
          : null;

      setActiveItemId(initialId ? Number(initialId) : null);
    }
  }, [open, items, initialActiveId]);

  // Fetch document/photo when active item changes
  useEffect(() => {
    if (!open || !activeItemId) {
      setActivePhotoUrl("");
      setActiveOtherPhotos([]);
      return;
    }

    if (photoCache[activeItemId]) {
      setActivePhotoUrl(photoCache[activeItemId].url);
      setActiveOtherPhotos(photoCache[activeItemId].otherPhotos || []);
      return;
    }

    let isMounted = true;
    setLoadingPhoto(true);

    Api.post(API_URLS.EMPLOYEES.GET_DOC_URL, { id: activeItemId })
      .then((res: any) => {
        if (!isMounted) return;
        const result = res?.data?.data || null;
        const url = result?.url || "";
        const otherPhotos =
          result?.attendanceVerification?.employee_verfification_temp_photos || [];

        setPhotoCache((prev) => ({
          ...prev,
          [activeItemId]: { url, otherPhotos },
        }));
        setActivePhotoUrl(url);
        setActiveOtherPhotos(otherPhotos);
      })
      .catch((err: any) => {
        console.error("Error fetching verification photo:", err);
        if (isMounted) {
          setActivePhotoUrl("");
          setActiveOtherPhotos([]);
        }
      })
      .finally(() => {
        if (isMounted) setLoadingPhoto(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, activeItemId, photoCache]);

  const randomAvatarColor = (name: string) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#d46b08",
      "#1890ff",
      "#eb2f96",
    ];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const handleRemoveItem = (idToRemove: number) => {
    const updated = selectedList.filter((item) => (item.id ?? item.key) !== idToRemove);
    setSelectedList(updated);
    setCheckedIds((prev) => prev.filter((id) => id !== idToRemove));
    if (updated.length === 0) {
      onClose();
      return;
    }
    if (activeItemId === idToRemove) {
      const nextId = updated[0]?.id ?? updated[0]?.key;
      setActiveItemId(nextId ? Number(nextId) : null);
    }
  };

  const handleToggleCheck = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((itId) => itId !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    const allIds = selectedList.map((it) => Number(it.id ?? it.key)).filter(Boolean);
    if (checkedIds.length === allIds.length) {
      setCheckedIds([]);
    } else {
      setCheckedIds(allIds);
    }
  };

  const updateItemConfig = (itemId: number, patch: Partial<ItemCustomConfig>) => {
    setCustomConfigs((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        ...patch,
      },
    }));
  };

  const handleQuickReason = (text: string) => {
    setReason(text);
    setReasonError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!actionType) {
      toast.warning("Please select an Action Type.");
      return;
    }

    if (actionType === "reject" && !reason.trim()) {
      setReasonError("Reason is required for rejection.");
      return;
    }

    const targetItems = selectedList.filter((item) =>
      checkedIds.includes(Number(item.id ?? item.key))
    );

    if (targetItems.length === 0) {
      toast.warning("No records selected to regularize.");
      return;
    }

    const requests = targetItems.map((item) => {
      const id = item.id ?? item.key;
      const config = customConfigs[id] || {};
      const effectiveAction = config.actionType || actionType;
      const isVerified = effectiveAction === "accept" ? "VERIFIED" : "REJECTED";
      const effectiveReason =
        config.reason !== undefined
          ? config.reason
          : reason || (isVerified === "VERIFIED" ? "Face photo verified and approved" : "Not approved");

      return {
        id: Number(id),
        is_verified: isVerified,
        reason: effectiveReason,
      };
    });

    console.log("Submitting face data regularize payload:", { requests });

    try {
      setIsSubmitting(true);
      const res = await (dispatch as any)(bulkUpdateEmployeesVerification({ requests })).unwrap();
      setIsSubmitting(false);

      const summary = res?.data?.summary || res?.summary;
      const results: any[] = res?.data?.results || res?.results || [];

      if (summary && summary.failed > 0) {
        const failedItems = results.filter((r) => r.status === "FAILED");
        const failedMsg = failedItems
          .map((r) => `ID ${r.id}: ${r.message || "Failed"}`)
          .slice(0, 3)
          .join(" | ");

        if (summary.successful === 0) {
          toast.error(
            `Bulk verification failed for all ${summary.failed} record(s): ${failedMsg}`
          );
        } else {
          toast.warning(
            `${summary.successful} succeeded, ${summary.failed} failed: ${failedMsg}`
          );
        }
      } else {
        toast.success(
          res?.message || `${requests.length} face data verification record(s) regularized successfully.`
        );
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setIsSubmitting(false);
      const msg = typeof err === "string" ? err : err?.message || "Failed to update face verification.";
      toast.error(msg);
    }
  };

  const isFormValid = () => {
    if (!actionType) return false;
    if (actionType === "reject" && !reason.trim()) return false;
    return true;
  };

  // Find currently active item
  const activeRecord = selectedList.find((it) => (it.id ?? it.key) === activeItemId) || selectedList[0];
  const activeEmp = activeRecord?.employee;
  const activeDetails = activeEmp?.details;
  const activeFirst = activeDetails?.firstname || "";
  const activeLast = activeDetails?.lastname || activeDetails?.lsastname || "";
  const activeFullName = `${activeFirst} ${activeLast}`.trim() || `Employee #${activeRecord?.id}`;
  const activeEmpId = activeEmp?.empId || activeRecord?.emp_id || "-";
  const activeDept =
    activeDetails?.sap_department_designation_wings?.department_name || activeDetails?.department || "";

  return (
    <Modal
      title={
        <div className="d-flex align-items-center justify-content-between pe-4">
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#FEE2E2",
                color: "#C0282C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              <i className="ti ti-scan" />
            </div>
            <div>
              <span style={{ fontSize: "17px", fontWeight: "600", color: "#111827" }}>
                Face Data Regularize
              </span>
              <span
                className="badge ms-2"
                style={{
                  backgroundColor: checkedIds.length > 1 ? "#E0E7FF" : "#ECFDF5",
                  color: checkedIds.length > 1 ? "#3730A3" : "#065F46",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderRadius: "12px",
                  padding: "4px 8px",
                }}
              >
                {checkedIds.length > 1
                  ? `Bulk Action (${checkedIds.length} Selected)`
                  : checkedIds.length === 1
                  ? `1 Record Selected (#${checkedIds[0]})`
                  : "No Records Selected"}
              </span>
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={false}
      width={860}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(90vh - 70px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 18px",
        },
      }}
    >
      <div className="row g-3 pt-1">
        {/* Left Column: Selectable & Scrollable Records List */}
        <div className="col-lg-5 col-md-5">
          <div
            style={{
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "10px 12px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  id="select_all_face_records"
                  checked={
                    selectedList.length > 0 &&
                    checkedIds.length === selectedList.length
                  }
                  onChange={handleToggleSelectAll}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                <label
                  htmlFor="select_all_face_records"
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#374151",
                    cursor: "pointer",
                    margin: 0,
                  }}
                >
                  Select All ({checkedIds.length}/{selectedList.length})
                </label>
              </div>
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                Click to inspect
              </span>
            </div>

            <div
              style={{
                maxHeight: "340px",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {selectedList.map((item, idx) => {
                const id = Number(item.id ?? item.key);
                const employee = item.employee;
                const details = employee?.details;
                const first = details?.firstname || "";
                const last = details?.lastname || details?.lsastname || "";
                const fullName = `${first} ${last}`.trim() || `Employee #${item.emp_id || id}`;
                const empId = employee?.empId || item.emp_id || "-";
                const dept =
                  details?.sap_department_designation_wings?.department_name ||
                  details?.department ||
                  "";
                const hasCustom = Boolean(customConfigs[id]);
                const isSelected = activeItemId === id;
                const isChecked = checkedIds.includes(id);
                const isEditingThis = editingItemId === id;

                return (
                  <div
                    key={id || idx}
                    onClick={() => setActiveItemId(id)}
                    style={{
                      backgroundColor: isSelected ? "#EFF6FF" : isChecked ? "#ffffff" : "#f9fafb",
                      border: isSelected
                        ? "2px solid #2563EB"
                        : isChecked
                        ? "1px solid #bfdbfe"
                        : "1px dashed #d1d5db",
                      opacity: isChecked ? 1 : 0.65,
                      boxShadow: isSelected
                        ? "0 2px 6px rgba(37,99,235,0.12)"
                        : "none",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      marginBottom: "6px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked={isChecked}
                          onChange={() => {}}
                          onClick={(e) => handleToggleCheck(id, e)}
                          style={{ width: "15px", height: "15px", cursor: "pointer", flexShrink: 0 }}
                        />
                        {details?.image ? (
                          <Avatar
                            src={`data:image/jpeg;base64,${details.image}`}
                            size={34}
                          />
                        ) : employee?.sap_employee_images?.image ? (
                          <Avatar
                            src={`data:image/jpeg;base64,${employee.sap_employee_images.image}`}
                            size={34}
                          />
                        ) : (
                          <Avatar
                            style={{
                              backgroundColor: randomAvatarColor(fullName),
                              fontWeight: 600,
                            }}
                            size={34}
                          >
                            {first ? first.charAt(0).toUpperCase() : "E"}
                          </Avatar>
                        )}
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: isSelected ? "#1E40AF" : "#111827",
                              lineHeight: 1.2,
                            }}
                          >
                            {fullName}
                          </div>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            <span className="badge bg-light text-dark me-1" style={{ fontSize: "9px" }}>
                              ID: {empId}
                            </span>
                            {dept && <span>{dept}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          title="Override individual settings"
                          onClick={() => setEditingItemId(isEditingThis ? null : id)}
                          style={{
                            fontSize: "12px",
                            color: hasCustom ? "#2563EB" : "#6B7280",
                          }}
                        >
                          <i className="ti ti-adjustments" />
                        </button>
                        {selectedList.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-danger"
                            title="Remove from selection"
                            onClick={() => handleRemoveItem(id)}
                            style={{ fontSize: "13px" }}
                          >
                            <i className="ti ti-x" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Date Details & Status */}
                    <div
                      className="d-flex align-items-center justify-content-between mt-1 pt-1"
                      style={{
                        borderTop: "1px dashed #f3f4f6",
                        fontSize: "10px",
                        color: "#6b7280",
                      }}
                    >
                      <span>
                        <b>Date:</b> {item.created_at ? formatDateTime(item.created_at) : "N/A"}
                      </span>
                      {hasCustom && (
                        <span
                          className={`badge ${
                            customConfigs[id]?.actionType === "reject" ? "bg-danger" : "bg-success"
                          }`}
                          style={{ fontSize: "9px" }}
                        >
                          {customConfigs[id]?.actionType === "reject" ? "Reject" : "Accept"}
                        </span>
                      )}
                    </div>

                    {/* Collapsible custom row config */}
                    {isEditingThis && (
                      <div
                        className="mt-2 pt-2"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          borderTop: "1px solid #bfdbfe",
                          fontSize: "11px",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong style={{ color: "#1e40af" }}>Override for {first || "this item"}:</strong>
                          {hasCustom && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-danger"
                              style={{ fontSize: "10px" }}
                              onClick={() => {
                                const copy = { ...customConfigs };
                                delete copy[id];
                                setCustomConfigs(copy);
                              }}
                            >
                              Reset
                            </button>
                          )}
                        </div>

                        <div className="row g-1">
                          <div className="col-12">
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: "11px", padding: "2px 6px" }}
                              value={customConfigs[id]?.actionType || actionType || "accept"}
                              onChange={(e) =>
                                updateItemConfig(id, {
                                  actionType: e.target.value as any,
                                })
                              }
                            >
                              <option value="accept">Accept (Verified)</option>
                              <option value="reject">Reject</option>
                            </select>
                          </div>
                          <div className="col-12 mt-1">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ fontSize: "11px", padding: "3px 6px" }}
                              placeholder="Reason for this photo"
                              value={customConfigs[id]?.reason ?? ""}
                              onChange={(e) =>
                                updateItemConfig(id, { reason: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="mt-auto pt-2"
              style={{
                borderTop: "1px solid #e5e7eb",
                fontSize: "11px",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{selectedList.length} record(s)</span>
              <span>{selectedList.length > 1 ? "Click item to view photo" : "Ready to verify"}</span>
            </div>
          </div>
        </div>

        {/* Right Column: Photo Preview & Approval Form */}
        <div className="col-lg-7 col-md-7">
          {/* Active Record Info Banner */}
          <div
            className="d-flex align-items-center justify-content-between p-2 mb-2"
            style={{
              backgroundColor: "#F3F4F6",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
            }}
          >
            <div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#111827" }}>
                {activeFullName}
              </div>
              <div style={{ fontSize: "11px", color: "#6B7280" }}>
                Emp ID: <strong>{activeEmpId}</strong> {activeDept && `• ${activeDept}`}
              </div>
            </div>
            {activeRecord?.created_at && (
              <span className="badge bg-light text-dark" style={{ fontSize: "10px" }}>
                Submitted: {formatDateTime(activeRecord.created_at)}
              </span>
            )}
          </div>

          {/* Photo Preview Container */}
          <div
            className="mb-3 position-relative"
            style={{
              width: "100%",
              height: "190px",
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {loadingPhoto ? (
              <div className="text-center">
                <Spin />
                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px" }}>
                  Loading submitted photo...
                </div>
              </div>
            ) : activePhotoUrl ? (
              <img
                src={activePhotoUrl}
                alt="Submitted Face"
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div className="text-center text-muted">
                <i className="ti ti-user" style={{ fontSize: "48px", color: "#CBD5E1" }} />
                <div style={{ fontSize: "12px", color: "#94A3B8", marginTop: "4px" }}>
                  No photo preview available
                </div>
              </div>
            )}

            {/* Other Photos Thumbnails if present */}
            {activeOtherPhotos.length > 0 && (
              <div
                className="position-absolute bottom-0 start-0 w-100 p-1 d-flex gap-1 overflow-auto"
                style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
              >
                {activeOtherPhotos.map((photoItem: any, pIdx: number) => (
                  <img
                    key={pIdx}
                    src={`data:image/png;base64,${photoItem.photo}`}
                    alt={`Photo ${pIdx + 1}`}
                    onClick={() => setActivePhotoUrl(`data:image/png;base64,${photoItem.photo}`)}
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      cursor: "pointer",
                      border: "1px solid #fff",
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Approval Form */}
          <form onSubmit={handleSubmit}>
            <div className="row g-2">
              {/* Action Type */}
              <div className="col-md-12">
                <label
                  className="form-label"
                  style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}
                >
                  Action Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={actionType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setActionType(val);
                    setReasonError("");
                    if (val === "reject") {
                      setReason("Face photo blur or unclear");
                    } else if (val === "accept") {
                      setReason("Face photo verified and approved");
                    }
                  }}
                  style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                >
                  <option value="">Select Option</option>
                  <option value="accept">Accept (Verify)</option>
                  <option value="reject">Reject</option>
                </select>
              </div>

              {/* Reason / Remarks */}
              <div className="col-md-12 mt-2">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label m-0" style={{ fontWeight: 600, fontSize: "12px", color: "#374151" }}>
                    Reason / Remarks {actionType === "reject" && <span className="text-danger">*</span>}
                  </label>
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>
                    {selectedList.length > 1 ? "Applies to all selected" : "Verification remark"}
                  </span>
                </div>
                <textarea
                  className="form-control"
                  placeholder="e.g. Face photo verified and approved"
                  rows={2}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (e.target.value.trim()) setReasonError("");
                  }}
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                />
                {reasonError && (
                  <small className="text-danger d-block mt-1">{reasonError}</small>
                )}

                {/* Quick Suggestion Chips */}
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {actionType === "accept" && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Face photo verified and approved")}
                      >
                        Verified & approved
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Face photo clear and approved")}
                      >
                        Clear & approved
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Approved as per employee ID")}
                      >
                        Approved as per ID
                      </button>
                    </>
                  )}
                  {actionType === "reject" && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Face photo blur or unclear")}
                      >
                        Blur / unclear
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Face not centered in frame")}
                      >
                        Face not centered
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickReason("Photo mismatch with ID record")}
                      >
                        Photo mismatch
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="col-md-12 pt-3">
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light px-3"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn px-4 d-flex align-items-center gap-2"
                    disabled={!isFormValid() || isSubmitting || checkedIds.length === 0}
                    style={{
                      backgroundColor: "#C0282C",
                      borderColor: "#C0282C",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "500",
                      borderRadius: "6px",
                      opacity: !isFormValid() || isSubmitting || checkedIds.length === 0 ? 0.6 : 1,
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Regularizing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check" />
                        {checkedIds.length > 1
                          ? `Regularize (${checkedIds.length})`
                          : checkedIds.length === 1
                          ? "Regularize (1)"
                          : "Regularize"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default BulkFaceDataRegularizeModal;
